import { AnimatePresence, motion } from "framer-motion";
import type { Player } from "../types";

interface LeaderboardProps {
	players: Player[];
	onPlayerClick?: (playerId: string) => void;
}

export function Leaderboard({ players, onPlayerClick }: LeaderboardProps) {
	const activePlayers = players.filter((p) => p.wins > 0 || p.losses > 0);
	const inactiveCount = players.length - activePlayers.length;

	if (players.length === 0) {
		return (
			<div className="card leaderboard">
				<h2>Leaderboard</h2>
				<p className="hint">Add players to see the leaderboard.</p>
			</div>
		);
	}

	if (activePlayers.length === 0) {
		return (
			<div className="card leaderboard">
				<h2>Leaderboard</h2>
				<p className="hint">No games played yet.</p>
				<p className="inactive-note">
					{inactiveCount} player{inactiveCount !== 1 ? "s" : ""} waiting
				</p>
			</div>
		);
	}

	const getRankEmoji = (index: number) => {
		if (index === 0) return "🥇";
		if (index === 1) return "🥈";
		if (index === 2) return "🥉";
		return `#${index + 1}`;
	};

	return (
		<div className="card leaderboard">
			<h2>Leaderboard</h2>
			<div className="leaderboard-list">
				<AnimatePresence mode="popLayout">
					{activePlayers.map((player, index) => (
						<motion.div
							key={player.id}
							layout
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							transition={{
								layout: { type: "spring", stiffness: 350, damping: 25 },
								opacity: { duration: 0.2 },
							}}
							className={`leaderboard-item ${index < 3 ? `rank-${index + 1}` : ""}`}
						>
							<span className="rank">{getRankEmoji(index)}</span>
							<button
								type="button"
								className="player-info"
								onClick={() => onPlayerClick?.(player.id)}
							>
								<span className="name">{player.name}</span>
								<span className="record">
									{player.wins}W - {player.losses}L
								</span>
							</button>
							<motion.span
								className="elo"
								key={player.elo}
								initial={{ scale: 1.3 }}
								animate={{ scale: 1 }}
								transition={{ duration: 0.3 }}
							>
								{player.elo}
							</motion.span>
						</motion.div>
					))}
				</AnimatePresence>
			</div>
			{inactiveCount > 0 && (
				<p className="inactive-note">
					{inactiveCount} player{inactiveCount !== 1 ? "s" : ""} with no games
				</p>
			)}
		</div>
	);
}
